import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Receipt, X, Download, Send, CheckCircle2, Plus, Trash2 } from "lucide-react";
import api, { apiError } from "../lib/api";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";
const SYM = { GBP: "£", USD: "$", EUR: "€" };
const BADGE = { draft: "bg-slate-200 text-slate-600", sent: "bg-sky-100 text-sky-800", paid: "bg-emerald-100 text-emerald-800", overdue: "bg-red-100 text-red-700" };

function NewInvoiceModal({ onSaved, onClose }) {
  const [f, setF] = useState({ client_company: "", client_name: "", client_email: "", client_address: "",
    client_country: "", currency: "GBP", vat_rate: 0, vat_number: localStorage.getItem("inv_vat_number") || "", due_date: "", payment_link: "", notes: "" });
  const [items, setItems] = useState([{ description: "", qty: 1, unit_price: "" }]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setItem = (i, k) => (e) => setItems(items.map((it, idx) => idx === i ? { ...it, [k]: e.target.value } : it));
  const subtotal = items.reduce((s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0), 0);
  const vat = subtotal * (parseFloat(f.vat_rate) || 0) / 100;
  const total = subtotal + vat;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      localStorage.setItem("inv_vat_number", f.vat_number || "");
      await api.post("/admin/invoices", { ...f, vat_rate: parseFloat(f.vat_rate) || 0, items: items.filter((i) => i.description.trim()).map((i) => ({
        description: i.description, qty: parseFloat(i.qty) || 1, unit_price: parseFloat(i.unit_price) || 0 })) });
      toast.success("Invoice created as draft");
      onSaved(); onClose();
    } catch (e2) { toast.error(apiError(e2)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-2xl my-8 space-y-3" data-testid="invoice-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">New invoice</h3>
          <button type="button" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><label className="text-xs font-bold text-slate-500">Client company *</label>
            <input className={inputCls} required value={f.client_company} onChange={set("client_company")} data-testid="inv-company" /></div>
          <div><label className="text-xs font-bold text-slate-500">Contact name</label>
            <input className={inputCls} value={f.client_name} onChange={set("client_name")} data-testid="inv-name" /></div>
          <div><label className="text-xs font-bold text-slate-500">Email *</label>
            <input className={inputCls} type="email" required value={f.client_email} onChange={set("client_email")} data-testid="inv-email" /></div>
          <div><label className="text-xs font-bold text-slate-500">Country</label>
            <input className={inputCls} value={f.client_country} onChange={set("client_country")} data-testid="inv-country" /></div>
        </div>
        <div><label className="text-xs font-bold text-slate-500">Billing address</label>
          <input className={inputCls} value={f.client_address} onChange={set("client_address")} data-testid="inv-address" /></div>
        <div>
          <label className="text-xs font-bold text-slate-500">Line items *</label>
          {items.map((it, i) => (
            <div key={i} className="flex gap-2 mt-1.5">
              <input className={`${inputCls} flex-1`} placeholder="Description" value={it.description} onChange={setItem(i, "description")} data-testid={`inv-item-desc-${i}`} />
              <input className={`${inputCls} w-16`} type="number" min="0" step="any" placeholder="Qty" value={it.qty} onChange={setItem(i, "qty")} data-testid={`inv-item-qty-${i}`} />
              <input className={`${inputCls} w-28`} type="number" min="0" step="0.01" placeholder="Unit price" value={it.unit_price} onChange={setItem(i, "unit_price")} data-testid={`inv-item-price-${i}`} />
              {items.length > 1 && <button type="button" onClick={() => setItems(items.filter((_, x) => x !== i))} className="text-red-400 hover:text-red-600"><Trash2 size={15} /></button>}
            </div>
          ))}
          <button type="button" onClick={() => setItems([...items, { description: "", qty: 1, unit_price: "" }])}
            className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:underline" data-testid="inv-add-item"><Plus size={13} /> Add line</button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><label className="text-xs font-bold text-slate-500">Currency</label>
            <select className={inputCls} value={f.currency} onChange={set("currency")} data-testid="inv-currency">
              <option value="GBP">GBP £</option><option value="USD">USD $</option><option value="EUR">EUR €</option>
            </select></div>
          <div><label className="text-xs font-bold text-slate-500">VAT</label>
            <select className={inputCls} value={f.vat_rate} onChange={set("vat_rate")} data-testid="inv-vat">
              <option value={0}>No VAT</option><option value={20}>20% VAT</option>
            </select></div>
          <div><label className="text-xs font-bold text-slate-500">Your VAT number</label>
            <input className={inputCls} value={f.vat_number} onChange={set("vat_number")} placeholder="GB123456789" data-testid="inv-vat-number" /></div>
          <div><label className="text-xs font-bold text-slate-500">Due date *</label>
            <input className={inputCls} type="date" required value={f.due_date} onChange={set("due_date")} data-testid="inv-due" /></div>
        </div>
        <div><label className="text-xs font-bold text-slate-500">Payment link (Stripe payment link, bank details page, etc.)</label>
          <input className={inputCls} value={f.payment_link} onChange={set("payment_link")} placeholder="https://buy.stripe.com/…" data-testid="inv-paylink" /></div>
        <div><label className="text-xs font-bold text-slate-500">Notes (shown on invoice)</label>
          <textarea className={inputCls} rows={2} value={f.notes} onChange={set("notes")} placeholder="e.g. Enterprise licence — 50 seats, 12 months. Bank transfer also accepted." data-testid="inv-notes" /></div>
        <div className="flex items-center justify-between">
          <p className="text-sm font-bold" data-testid="inv-total-preview">
            Total: {SYM[f.currency]}{total.toFixed(2)}{" "}
            <span className="text-xs text-slate-400 font-medium">
              {parseFloat(f.vat_rate) > 0 ? `· Subtotal ${SYM[f.currency]}${subtotal.toFixed(2)} + VAT ${SYM[f.currency]}${vat.toFixed(2)}` : "· VAT not applicable"}
            </span>
          </p>
          <button type="submit" disabled={busy} className="bg-slate-900 text-white font-bold px-6 py-2.5 rounded-xl hover:bg-emerald-700 transition-colors disabled:opacity-50" data-testid="inv-create-btn">
            {busy ? "Creating…" : "Create draft"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function AdminInvoices() {
  const [rows, setRows] = useState([]);
  const [show, setShow] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const load = async () => {
    try { setRows((await api.get("/admin/invoices")).data); } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const dl = async (inv) => {
    try {
      const r = await api.get(`/admin/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.number}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };
  const send = async (inv) => {
    if (!window.confirm(`Email invoice ${inv.number} (${SYM[inv.currency]}${inv.total.toFixed(2)}) to ${inv.client_email}?`)) return;
    setBusyId(inv.id);
    try { await api.post(`/admin/invoices/${inv.id}/send`); await load(); toast.success("Invoice emailed with PDF attached"); }
    catch (e) { toast.error(apiError(e)); }
    setBusyId(null);
  };
  const markPaid = async (inv) => {
    setBusyId(inv.id);
    try { await api.put(`/admin/invoices/${inv.id}/status`, { status: "paid" }); await load(); toast.success("Marked as paid"); }
    catch (e) { toast.error(apiError(e)); }
    setBusyId(null);
  };

  return (
    <div data-testid="admin-invoices">
      <div className="flex items-center gap-3 mb-4">
        <h2 className="font-display font-bold text-xl flex items-center gap-2"><Receipt size={20} /> Invoices</h2>
        <button onClick={() => setShow(true)} className="bg-emerald-700 text-white text-sm font-bold px-4 py-2 rounded-xl hover:bg-emerald-800 transition-colors" data-testid="inv-new-btn">+ New invoice</button>
      </div>
      <p className="text-sm text-slate-500 mb-5 max-w-2xl">Create branded invoices from Voliciti Group Ventures Ltd, paste your payment link, and email them with the PDF attached. Mark them paid when the money arrives.</p>
      <div className="space-y-3">
        {rows.map((inv) => (
          <div key={inv.id} className="bg-white border border-slate-200 rounded-2xl p-4 sm:p-5 flex flex-col md:flex-row md:items-center gap-3" data-testid={`inv-row-${inv.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-bold">{inv.number}</p>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${BADGE[inv.status] || BADGE.draft}`} data-testid={`inv-status-${inv.id}`}>{inv.status}</span>
                <span className="text-sm font-bold text-emerald-700">{SYM[inv.currency]}{inv.total.toFixed(2)}</span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">{inv.client_company} · {inv.client_email}</p>
              <p className="text-xs text-slate-400 mt-0.5">Issued {inv.issue_date} · Due {inv.due_date}{inv.paid_at && <> · Paid {(inv.paid_at || "").slice(0, 10)}</>}</p>
            </div>
            <div className="flex gap-2 shrink-0 flex-wrap">
              <button onClick={() => dl(inv)} className="inline-flex items-center gap-1.5 text-xs font-bold border border-slate-200 px-3 py-2 rounded-lg hover:bg-slate-50" data-testid={`inv-dl-${inv.id}`}><Download size={12} /> PDF</button>
              {inv.status !== "paid" && (
                <button onClick={() => send(inv)} disabled={busyId === inv.id} className="inline-flex items-center gap-1.5 text-xs font-bold border border-sky-200 text-sky-700 px-3 py-2 rounded-lg hover:bg-sky-50 disabled:opacity-50" data-testid={`inv-send-${inv.id}`}>
                  <Send size={12} /> {inv.status === "draft" ? "Send" : "Resend"}
                </button>
              )}
              {inv.status !== "paid" && (
                <button onClick={() => markPaid(inv)} disabled={busyId === inv.id} className="inline-flex items-center gap-1.5 text-xs font-bold border border-emerald-200 text-emerald-700 px-3 py-2 rounded-lg hover:bg-emerald-50 disabled:opacity-50" data-testid={`inv-paid-${inv.id}`}>
                  <CheckCircle2 size={12} /> Mark paid
                </button>
              )}
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-slate-400 py-8 text-center">No invoices yet.</p>}
      </div>
      {show && <NewInvoiceModal onSaved={load} onClose={() => setShow(false)} />}
    </div>
  );
}
